import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  People as PeopleIcon,
  Settings as SettingsIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Psychology as PsychologyIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';
import EngagementMetrics from '../components/EngagementMetrics';
import ContentMetrics from '../components/ContentMetrics';
import FileMetrics from '../components/FileMetrics';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  subtitle?: string;
}

function StatCard({ title, value, icon, color = 'primary', subtitle }: StatCardProps) {
  const getColor = (color: string) => {
    const colors = {
      primary: '#1976d2',
      secondary: '#dc004e',
      success: '#2e7d32',
      warning: '#ed6c02',
      error: '#d32f2f',
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: getColor(color) + '15',
              color: getColor(color),
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 600 }}>
          {value.toLocaleString()}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [engagementDays, setEngagementDays] = useState(30);

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: statsApi.getDashboardStats,
    refetchInterval: 30000, // Обновляем каждые 30 секунд
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки статистики: {(error as Error).message}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Нет данных для отображения
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 4, fontWeight: 600 }}>
        Обзор системы
      </Typography>

      <Grid container spacing={3}>
        {/* Row 1: Main Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего пользователей"
            value={stats.total_users}
            icon={<PeopleIcon />}
            color="primary"
            subtitle="Зарегистрированных пользователей"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Активные сессии"
            value={stats.active_sessions}
            icon={<SettingsIcon />}
            color="warning"
            subtitle="Обрабатываются сейчас"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего платежей"
            value={stats.total_payments}
            icon={<PaymentIcon />}
            color="success"
            subtitle="Проведенных транзакций"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="LLM запросы"
            value={stats.total_llm_requests}
            icon={<PsychologyIcon />}
            color="secondary"
            subtitle="Обращений к AI моделям"
          />
        </Grid>

        {/* Row 2: Processing Stats */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Успешные сессии"
            value={stats.successful_sessions}
            icon={<CheckCircleIcon />}
            color="success"
            subtitle="Обработано без ошибок"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Неудачные сессии"
            value={stats.failed_sessions}
            icon={<ErrorIcon />}
            color="error"
            subtitle="Завершились с ошибкой"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Минут аудио"
            value={stats.total_audio_minutes?.toFixed(0) || '0'}
            icon={<VolumeUpIcon />}
            color="primary"
            subtitle="Обработано аудиоматериала"
          />
        </Grid>
      </Grid>

      {/* Additional Info Cards */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Системная информация
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Производительность
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Успешность обработки: {' '}
                    <Typography component="span" variant="body2" fontWeight={600} color="success.main">
                      {stats.successful_sessions + stats.failed_sessions > 0
                        ? ((stats.successful_sessions / (stats.successful_sessions + stats.failed_sessions)) * 100).toFixed(1)
                        : 0}%
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Среднее время аудио: {' '}
                    <Typography component="span" variant="body2" fontWeight={600}>
                      {stats.total_audio_minutes && stats.successful_sessions > 0
                        ? (stats.total_audio_minutes / stats.successful_sessions).toFixed(1)
                        : 0} мин/сессия
                    </Typography>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Активность системы
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    LLM запросов на пользователя: {' '}
                    <Typography component="span" variant="body2" fontWeight={600}>
                      {stats.total_users > 0
                        ? (stats.total_llm_requests / stats.total_users).toFixed(1)
                        : 0}
                    </Typography>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Статус системы: {' '}
                    <Typography
                      component="span"
                      variant="body2"
                      fontWeight={600}
                      color={stats.active_sessions > 0 ? "warning.main" : "success.main"}
                    >
                      {stats.active_sessions > 0 ? "Активная обработка" : "Готова к работе"}
                    </Typography>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Engagement Metrics Section */}
      <Divider sx={{ my: 4 }} />
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Вовлеченность пользователей
        </Typography>
        <EngagementMetrics days={engagementDays} onDaysChange={setEngagementDays} />
      </Box>

      {/* Content Metrics Section */}
      <Divider sx={{ my: 4 }} />
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Контент и использование
        </Typography>
        <ContentMetrics />
      </Box>

      {/* File Metrics Section */}
      <Divider sx={{ my: 4 }} />
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Файлы и аудио
        </Typography>
        <FileMetrics />
      </Box>
    </Box>
  );
}